import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import { Document, Page, pdfjs } from 'react-pdf';
import { Modal } from 'components/migrated/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { GetToken, GetUserInfo } from 'components/util/Helpers/Auth';
import { InjectedRouter } from 'react-router';
import { PatternFormat } from 'react-number-format';
import { useStyles } from './styles';
import { getDocument, signDocument } from 'controllers/compliance';

pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.min.js';

export interface IRouterProps {
  router: InjectedRouter;
}

export const DocumentSignature = ({ router }: IRouterProps) => {
  const classes = useStyles();
  const complianceId = window.location.href.split('/').pop();
  const token = GetToken();
  const [personId, setPersonId] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [openSignature, setOpenSignature] = useState(false);
  const [tryingToSign, setTryingToSign] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseError, setResponseError] = useState(false);
  const [file, setFile] = useState();
  const formRef = useRef(null as unknown as HTMLFormElement);

  useEffect(() => {
    const asyncEffect = async () => {
      if (!personId) {
        const { id } = await GetUserInfo();
        setPersonId(id);
      }
      try {
        const get = await getDocument(token, complianceId);
        setFile(JSON.parse(get.body));
      } catch (error) {
        console.error(error);
      }
    };
    asyncEffect();
  }, []);

  const onDocumentLoadSuccess = async (pdf: any) => {
    await setNumPages(pdf.numPages);

    const acceptButton = document.querySelector('#accept-button') as HTMLButtonElement;
    const root = document.getElementById('react-pdf__Document');
    const lastPage = document.querySelectorAll(`[data-page-number="${pdf.numPages}"]`)[0];
    const observer = new IntersectionObserver(
      (payload) => {
        if (payload[0].intersectionRatio > 0) {
          setButtonDisabled(false);
          acceptButton.style.cursor = 'pointer';
          acceptButton.style.background = '#0A775A';
          observer.unobserve(lastPage);
        }
      },
      {
        root,
        threshold: 0
      }
    );
    setTimeout(() => {
      observer.observe(lastPage);
    }, 2000);
  };

  type map = {
    [key: string]: string | undefined;
  };

  const handleDocumentSignature = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    const inputs = [...formRef.current.querySelectorAll('input')];
    let requestBody = inputs.reduce(
      (prev, curr) => {
        const value = ['cpfNumber'].includes(curr.name) ? curr.value.replace(/\D/g, '') : curr.value;
        prev[curr.name] = value;
        return prev;
      },
      {
        complianceId: complianceId,
        personId: personId,
        logonPersonId: personId
      } as unknown as map
    );

    try {
      const response = await signDocument(token, JSON.stringify(requestBody));
      const body = JSON.parse(response.body);
      if ('error' in body && body.error) {
        throw new Error(body.message);
      }
      setResponseMessage(body.message);
    } catch (error: any) {
      setResponseError(true);
      console.log(error.message);
      setResponseMessage('Não foi possível assinar o documento. Por favor tente novamente.');
    }
    setTryingToSign(true);
  };

  return (
    <div className={classes.container}>
      <>
        <div className={classes.doc_preview}>
          {file ? (
            <Document file={file} onLoadSuccess={onDocumentLoadSuccess} loading={<CircularProgress />}>
              {Array.from(new Array(numPages), (el, index) => (
                <Page key={`page_${index + 1}`} pageNumber={index + 1} />
              ))}
            </Document>
          ) : (
            <CircularProgress />
          )}
        </div>
        <div className={classes.doc_footer}>
          <Tooltip title={'Leia todo o documento para prosseguir'} placement={'top'}>
            <div>
              <Button
                className={classes.doc_button}
                id={'accept-button'}
                variant={'contained'}
                disabled={buttonDisabled}
                onClick={() => setOpenSignature(true)}
              >
                aceitar termo
              </Button>
            </div>
          </Tooltip>
        </div>
      </>
      <Modal open={openSignature} onClose={() => setOpenSignature(false)} title={'DADOS DO ASSINANTE'}>
        <div className={classes.modal_default}>
          <form ref={formRef} className={classes.modal_form} onSubmit={handleDocumentSignature}>
            <span>Para prosseguir, preencha as informações abaixo:</span>
            <input
              className={classes.modal_input}
              required
              name="representativeName"
              placeholder="Nome completo"
              pattern="^[a-zA-Z.\s]+$"
            />
            <PatternFormat
              required
              className={classes.modal_input}
              name="cpfNumber"
              placeholder="CPF"
              valueIsNumericString
              format="###.###.###-##"
              pattern="^[0-9]{3}\.[0-9]{3}\.[0-9]{3}-[0-9]{2}$"
              title="999.999.999-99"
            />
            <input
              className={classes.modal_input}
              required
              pattern="^[a-zA-Z0-9.!#$%&'*+-/=?^_`{|}~]+@[a-z0-9]+\.[a-z]+(\.[a-z]+)?$"
              name="mailAddress"
              placeholder="E-mail"
              title="exemplo@email.com"
            />
            <input
              className={classes.modal_input}
              required
              name="roleDescription"
              placeholder="Função na empresa"
              pattern="^[a-zA-Z.\s]+$"
            />
            {!tryingToSign && (
              <Button className={classes.modal_button} type="submit">
                aceitar termo
              </Button>
            )}
          </form>
          {tryingToSign && <CircularProgress />}
        </div>
      </Modal>
      <Modal
        open={tryingToSign}
        title={'ASSINATURA DO DOCUMENTO'}
        onClose={() => {
          responseError ? setTryingToSign(false) : router.push('/compliance');
        }}
      >
        <body className={classes.modal_default}>
          <div className={classes.modal_response}>
            {responseError ? (
              <FontAwesomeIcon icon={faCircleXmark} size="3x" style={{ color: 'red' }} />
            ) : (
              <FontAwesomeIcon icon={faCheckCircle} size="3x" style={{ color: 'green' }} />
            )}
            <span>{responseMessage}</span>
          </div>
        </body>
      </Modal>
    </div>
  );
};
